import { MoneyValue } from '@round/creatorbase-ui';
import { useCreatorbaseCurrencies } from '@round/creatorbase-helpers/api-hooks';

export type Rate = {
    cost: number | null | undefined;
    currencyId: number | null | undefined;
};

type GetDefaultCostParams = {
    areFeesEnabled: boolean;
    campaignCurrencyId: number | undefined;
    getCurrencyOptionsById: ReturnType<typeof useCreatorbaseCurrencies>['getCurrencyOptionsById'];
};

/**
 * Seeds a cost from a creator's own rate. Fees let a rate prefill in its own currency; without fees
 * the cost is locked to the campaign's currency, so a differently-priced rate is left blank instead,
 * since nothing here knows the exchange rate to convert it correctly.
 */
export const getDefaultCost = (
    rate: Rate | null | undefined,
    { areFeesEnabled, campaignCurrencyId, getCurrencyOptionsById }: GetDefaultCostParams
): MoneyValue => {
    const campaignCurrency = getCurrencyOptionsById(campaignCurrencyId);

    if (typeof rate?.cost !== 'number' || rate.currencyId == null) {
        return { currency: campaignCurrency, value: '' };
    }

    if (areFeesEnabled) {
        return { currency: getCurrencyOptionsById(rate.currencyId), value: rate.cost.toString() };
    }

    if (rate.currencyId !== campaignCurrencyId) {
        return { currency: campaignCurrency, value: '' };
    }

    return { currency: campaignCurrency, value: rate.cost.toString() };
};

type IsCurrencyMismatchParams = {
    isCostEditable: boolean;
    areFeesEnabled: boolean;
    campaignCurrencyId: number | undefined;
};

/** A rate priced in a currency other than the campaign's can't prefill without fees to convert it. */
export const isCurrencyMismatch = (
    rate: Rate | null | undefined,
    { isCostEditable, areFeesEnabled, campaignCurrencyId }: IsCurrencyMismatchParams
) =>
    isCostEditable &&
    !areFeesEnabled &&
    typeof rate?.cost === 'number' &&
    rate.currencyId != null &&
    rate.currencyId !== campaignCurrencyId;
